import { useContext, useEffect, useState } from "react";
import GeneralContext from "../../../context/GeneralContext/GeneralContext";
import api from "../../../services/api";
import { displayMessage } from "../../../helpers/messages";
import SL_Button from "../../../components/buttons/Button";
import { SL_Input } from "../../../components/input/Input";
import { Form, Modal, Select, Skeleton, Switch, Tag } from "antd";

export function DetailGymExerciseModal({ onCloseModal, exerciseId }) {
	const [isLoading, setIsLoading] = useState(true);
	const [masterExercise, setMasterExercise] = useState(null);
	const [catalogOptions, setCatalogOptions] = useState([]);
	const [form] = Form.useForm();
	const { setShowLoading } = useContext(GeneralContext);

	useEffect(() => {
		loadCatalog();
		loadExercise();
	}, [exerciseId]);

	async function loadCatalog() {
		try {
			const response = await api.exercises.list();
			setCatalogOptions(
				response.data.map((e) => ({ value: e.id, label: e.name, data: e }))
			);
		} catch (error) {
			console.error("Erro ao carregar catálogo:", error);
		}
	}

	async function loadExercise() {
		setIsLoading(true);
		try {
			const response = await api.gymExercises.find(exerciseId);
			const exercise = response.data;
			setMasterExercise(exercise.exercise || null);
			form.setFieldsValue({
				exercise_id:   exercise.exercise_id,
				name:          exercise.name,
				description:   exercise.description,
				video_url:     exercise.video_url,
				sets:          exercise.sets,
				reps:          exercise.reps,
				rest_seconds:  exercise.rest_seconds,
				order_index:   exercise.order_index,
				show_set_info: exercise.show_set_info !== false,
			});
		} catch (error) {
			console.error("Erro ao carregar exercício:", error);
		} finally {
			setIsLoading(false);
		}
	}

	// Ao trocar o exercício do catálogo, atualiza os campos herdados
	function onSelectFromCatalog(value, option) {
		const exercise = option.data;
		setMasterExercise(exercise);
		form.setFieldsValue({
			exercise_id:  exercise.id,
			name:         exercise.name,
			description:  exercise.description  ?? form.getFieldValue("description"),
			video_url:    exercise.video_url    ?? form.getFieldValue("video_url"),
			sets:         exercise.sets         ?? form.getFieldValue("sets"),
			reps:         exercise.reps         ?? form.getFieldValue("reps"),
			rest_seconds: exercise.rest_seconds ?? form.getFieldValue("rest_seconds"),
		});
	}

	async function onFinishForm(values) {
		try {
			setShowLoading(true);
			await api.gymExercises.update(exerciseId, values);
			await onCloseModal(true);
		} catch (error) {
			console.error("Erro ao atualizar exercício:", error);
		} finally {
			setShowLoading(false);
		}
	}

	return (
		<Modal
			title={
				<div className="text-xl font-bold text-blue-700 mr-3">
					{displayMessage("EDIT_EXERCISE")}
				</div>
			}
			open={true}
			onCancel={() => onCloseModal(false)}
			centered={true}
			footer={
				<div className="flex flex-col ss:flex-row gap-2 justify-end">
					<SL_Button
						className="w-full ss:w-[150px] bg-gray-500 border-transparent"
						onClick={() => onCloseModal(false)}
					>
						{displayMessage("CLOSE")}
					</SL_Button>
					<SL_Button
						className="w-full ss:w-[150px] m-0 bg-blue-600"
						onClick={() => form.submit()}
					>
						{displayMessage("SAVE")}
					</SL_Button>
				</div>
			}
			maskClosable={false}
			width={600}
		>
			{isLoading ? (
				<Skeleton active />
			) : (
				<Form
					onFinish={onFinishForm}
					layout="vertical"
					form={form}
					className={`flex gap-2 flex-col`}
				>
					{/* Campo oculto para guardar o exercise_id */}
					<Form.Item name="exercise_id" hidden>
						<SL_Input type="number" />
					</Form.Item>

					{/* Exibe o exercício mestre vinculado e permite trocar */}
					<div className="mb-1">
						<span className="text-blue-700 font-semibold text-sm">
							{displayMessage("SELECT_EXERCISE_FROM_CATALOG")}:
						</span>
						{masterExercise && (
							<Tag color="blue" className="ml-2">
								{displayMessage("INHERITED_FROM_CATALOG")}: {masterExercise.name}
							</Tag>
						)}
						<Select
							showSearch
							className="w-full mt-1"
							placeholder={displayMessage("SEARCH_EXERCISE")}
							optionFilterProp="label"
							options={catalogOptions}
							value={form.getFieldValue("exercise_id")}
							onSelect={onSelectFromCatalog}
							allowClear
						/>
					</div>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("EXERCISE_NAME")}:
							</span>
						}
						name="name"
						className={`w-full m-0`}
						required
						rules={[
							{ required: true, message: displayMessage("NAME_MANDATORY") },
						]}
					>
						<SL_Input type="text" className="text-blue-700" />
					</Form.Item>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("DESCRIPTION")}:
							</span>
						}
						name="description"
						className={`w-full m-0`}
						extra={
							masterExercise?.description
								? <span className="text-gray-400 text-xs">{displayMessage("INHERITED_FROM_CATALOG")}: {masterExercise.description}</span>
								: null
						}
					>
						<SL_Input textarea={true} rows={2} className="text-blue-700" />
					</Form.Item>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("VIDEO_URL")}:
							</span>
						}
						name="video_url"
						className={`w-full m-0`}
						extra={
							masterExercise?.video_url
								? <span className="text-gray-400 text-xs">{displayMessage("INHERITED_FROM_CATALOG")}: {masterExercise.video_url}</span>
								: null
						}
					>
						<SL_Input type="url" className="text-blue-700" placeholder="https://youtube.com/watch?v=..." />
					</Form.Item>

					<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("SETS")}:
								</span>
							}
							name="sets"
							className={`w-full m-0`}
							extra={
								masterExercise?.sets != null
									? <span className="text-gray-400 text-xs">{displayMessage("INHERITED_FROM_CATALOG")}: {masterExercise.sets}</span>
									: null
							}
						>
							<SL_Input type="number" className="text-blue-700" />
						</Form.Item>

						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("REPS")}:
								</span>
							}
							name="reps"
							className={`w-full m-0`}
							extra={
								masterExercise?.reps
									? <span className="text-gray-400 text-xs">{displayMessage("INHERITED_FROM_CATALOG")}: {masterExercise.reps}</span>
									: null
							}
						>
							<SL_Input type="text" className="text-blue-700" placeholder="Ex: 10-12, 15, até a falha" />
						</Form.Item>
					</div>

					<div className="gap-4 flex w-full flex-col smb:flex-row mt-2">
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("REST_SECONDS")}:
								</span>
							}
							name="rest_seconds"
							className={`w-full m-0`}
							extra={
								masterExercise?.rest_seconds != null
									? <span className="text-gray-400 text-xs">{displayMessage("INHERITED_FROM_CATALOG")}: {masterExercise.rest_seconds}s</span>
									: null
							}
						>
							<SL_Input type="number" className="text-blue-700" />
						</Form.Item>

						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("ORDER_INDEX")}:
								</span>
							}
							name="order_index"
							className={`w-full m-0`}
						>
							<SL_Input type="number" className="text-blue-700" />
						</Form.Item>
					</div>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("SHOW_SET_INFO")}:
							</span>
						}
						name="show_set_info"
						valuePropName="checked"
						className="m-0 mt-2"
					>
						<Switch />
					</Form.Item>
				</Form>
			)}
		</Modal>
	);
}


